import { useState } from "react";

import { ClientResponse } from "@/Common/Api/ClientResponse";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ISaveEmailOptions } from "@/Common/Models/Employee/ISaveEmailOptions";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { Button } from "@/DesignSystem";
import { EmailInputSection } from "./EmailInputSection";

import styles from "./RecoveryEmailPopups.module.scss";

interface IRecoveryEmailPopupProps {
    emailOptions: ISaveEmailOptions;
    stringProvider?: ResourceProvider;

    onEmailChange: (emailAddress: string) => void;
    onEmailAdded: (emailOptions: ISaveEmailOptions) => Promise<ClientResponse<any>>;
    onIsNotifiedByEmailChanged: (checked: boolean) => void;
    onIsOkToContactChanged: (checked: boolean) => void;
    onContinueWithoutEmail: () => void;
}

function RecoveryEmailPopup(props: IRecoveryEmailPopupProps): JSX.Element {
    const { getText } = useTranslation(props.stringProvider);

    const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const disableAddEmailButton: boolean =
        !isEmailValid
        || IsNullOrWhiteSpace(props.emailOptions.emailAddress)
        || isProcessing;

    async function addEmail(): Promise<void> {
        if (disableAddEmailButton)
            return;

        setIsProcessing(true);
        const response = await props.onEmailAdded(props.emailOptions);
        if (!response.isSuccess)
            setError(response.errors?.[0]?.Message);
        setIsProcessing(false);
    }

    function renderFooter(): JSX.Element {
        return (
            <div className={styles.footerContainer}>
                <Button
                    variant="neutral"
                    category="secondary"
                    label={getText("ContinueWithoutEmailLabel")}
                    onClick={props.onContinueWithoutEmail}
                    dataCy="login-cancelButton"
                />
                <Button
                    variant="brand"
                    category="primary"
                    label={getText("AddEmailLabel")}
                    onClick={addEmail}
                    disabled={disableAddEmailButton}
                    dataCy="login-continueButton"
                />
            </div>
        );
    }

    return (
        <Modal
            title={getText("RecoveryEmailTitle")}
            centerContent
            fixedOverlayPosition
            showOverlay
            disableOverlayClose
            footer={renderFooter()}
            dataCy="login-RequiredEmailPopupControl"
        >
            <div className={styles.requiredEmailPopupElements}>
                <p className={styles.text}>{getText("RecoveryEmailMessage")}</p>
                <p className={styles.text}>{getText("AdvisoryRecoveryEmailMessage")}</p>
                <EmailInputSection
                    error={error}
                    emailAddress={props.emailOptions.emailAddress}
                    isEmailValid={isEmailValid}
                    isNotifiedByEmail={props.emailOptions.isNotifiedByEmail}
                    isOkToContact={props.emailOptions.isOkToContact}
                    allowEmptyEmail={!sessionStore.isRecoveryEmailRequired}
                    stringProvider={props.stringProvider}
                    onEmailChange={(emailAddress, isEmailValid) => {
                        props.onEmailChange(emailAddress);
                        setIsEmailValid(isEmailValid);
                        setError(null);
                    }}
                    setIsNotifiedByEmail={props.onIsNotifiedByEmailChanged}
                    setIsOkToContact={props.onIsOkToContactChanged}
                    onEnterPressed={addEmail}
                />
            </div>
        </Modal>
    );
}

export { RecoveryEmailPopup };
